import {
  CashBankAccountEntity,
  CashBankAccountsResultEntity,
  CashBankPaginationEntity,
  CashBankSummaryEntity,
  CashBankTransactionEntity,
  CashBankTransactionsResultEntity,
} from "../../domain";
import {
  CashBankAccountDTO,
  CashBankAccountsResponseDTO,
  CashBankPaginationDTO,
  CashBankSummaryResponseDTO,
  CashBankTransactionDTO,
  CashBankTransactionsResponseDTO,
} from "../models/CashAndBankDTOs";

export class CashAndBankMapper {
  static toAccountEntity(dto: CashBankAccountDTO): CashBankAccountEntity {
    return {
      id: dto.id,
      bank_name: dto.bank_name,
      zoho_bankaccountid: dto.zoho_bankaccountid,
      account_number: dto.account_number,
      currency_id: dto.currency_id,
      account_type: dto.account_type,
      is_active: dto.is_active,
      is_auto: dto.is_auto,
      balance: Number(dto.balance ?? 0),
      bank_balance: Number(dto.bank_balance ?? 0),
      is_primary_account: dto.is_primary_account,
      statement_suggestion_count: Number(dto.statement_suggestion_count ?? 0),
      is_paypal_account: dto.is_paypal_account,
      currency_code: dto.currency_code,
      opening_balance: dto.opening_balance,
      opening_balance_date: dto.opening_balance_date,
      opening_balance_id: dto.opening_balance_id,
      pending_review: dto.pending_review,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      pending_customer_payments: {
        count: Number(dto.pending_customer_payments?.count ?? 0),
        total_amount: Number(dto.pending_customer_payments?.total_amount ?? 0),
      },
      pending_vendor_payments: {
        count: Number(dto.pending_vendor_payments?.count ?? 0),
        total_amount: Number(dto.pending_vendor_payments?.total_amount ?? 0),
      },
    };
  }

  static toAccountsResultEntity(
    response: CashBankAccountsResponseDTO,
  ): CashBankAccountsResultEntity {
    return {
      accounts: (response.data?.accounts ?? []).map((account) =>
        this.toAccountEntity(account),
      ),
      total_count: Number(response.data?.total_count ?? 0),
    };
  }

  static toTransactionEntity(
    dto: CashBankTransactionDTO,
  ): CashBankTransactionEntity {
    return {
      id: dto.id,
      transaction_id: dto.transaction_id,
      date: dto.date,
      description: dto.description,
      amount: Number(dto.amount ?? 0),
      user_status: dto.user_status,
      currency_code: dto.currency_code,
      currency_symbol: dto.currency_symbol,
      zoho_partyid: dto.zoho_partyid,
      zoho_partyid_confidence: dto.zoho_partyid_confidence,
      zoho_partyid_source: dto.zoho_partyid_source,
      zoho_party_id_confidence: dto.zoho_party_id_confidence,
      zoho_partytype: dto.zoho_partytype,
      zoho_category: dto.zoho_category,
      zoho_gl: dto.zoho_gl,
      updated_at: dto.updated_at,
      category_type: dto.category_type,
      review_status: dto.review_status,
      is_split: Boolean(dto.is_split),
      parent_transaction_id: dto.parent_transaction_id ?? null,
      parent_transaction: dto.parent_transaction
        ? {
            id: dto.parent_transaction.id,
            transaction_id: dto.parent_transaction.transaction_id,
            date: dto.parent_transaction.date,
            description: dto.parent_transaction.description,
            amount: Number(dto.parent_transaction.amount ?? 0),
          }
        : null,
    };
  }

  static toPaginationEntity(dto: CashBankPaginationDTO): CashBankPaginationEntity {
    return {
      total: Number(dto.total ?? 0),
      limit: Number(dto.limit ?? 10),
      offset: Number(dto.offset ?? 0),
      current_page: Number(dto.current_page ?? 1),
      total_pages: Number(dto.total_pages ?? 0),
      has_more: Boolean(dto.has_more),
    };
  }

  static toTransactionsResultEntity(
    response: CashBankTransactionsResponseDTO,
  ): CashBankTransactionsResultEntity {
    return {
      transactions: (response.data ?? []).map((transaction) =>
        this.toTransactionEntity(transaction),
      ),
      pagination: this.toPaginationEntity(
        response.pagination ?? {
          total: 0,
          limit: 10,
          offset: 0,
          current_page: 1,
          total_pages: 0,
          has_more: false,
        },
      ),
    };
  }

  static toSummaryEntity(
    response: CashBankSummaryResponseDTO,
  ): CashBankSummaryEntity {
    return {
      pending_customer_payments: {
        count: Number(response.data?.total_pending_customer_payments ?? 0),
        total_amount: Number(response.data?.total_pending_customer_amount ?? 0),
      },
      pending_vendor_payments: {
        count: Number(response.data?.total_pending_vendor_payments ?? 0),
        total_amount: Number(response.data?.total_pending_vendor_amount ?? 0),
      },
      currency_code: response.data?.currency_code ?? "INR",
      accounts_count: Number(response.data?.accounts_count ?? 0),
    };
  }
}
